import { IStairConfig } from '@stair-craft/common/util';

import type { IStairNorms } from '../../norms';
import { ITurnStepSplit, resolveTurnSplit } from '../shared-pick-in-norm-flight2-count/pick-in-norm-flight2-count';

export const U_WINDER_COUNT: number = 6;
export const WINDER_ANGLE_DEG: number = 30;

export interface IUShapeLayout {
    stepCount: number;
    riserHeight: number;
    treadDepth: number;
    flight1Count: number;
    flight2Count: number;
    turnStepCount: number;
}

/**
 * Coupled solution for (stepCount, riserHeight, treadDepth): angle ↔ stepCount,
 * treadDepth follows. See l-shape.calculator.ts for rationale. Extracted from
 * `calculateLayout` to keep its complexity low.
 */
function resolveStepDimensions(
    config: IStairConfig,
    norms: IStairNorms,
    totalRise: number
): { stepCount: number; riserHeight: number; treadDepth: number } {
    if (config.stepCount != null) {
        const stepCount: number = Math.max(3, config.stepCount);
        const riserHeight: number = totalRise / stepCount;
        const treadDepth: number =
            config.treadDepth ??
            (config.stairAngle != null
                ? riserHeight / Math.tan(config.stairAngle * (Math.PI / 180))
                : Math.max(norms.treadDepthMin, Math.min(norms.treadDepthMax, norms.blondelIdeal - 2 * riserHeight)));
        return { stepCount, riserHeight, treadDepth };
    }
    if (config.stairAngle != null) {
        const tanA: number = Math.tan(config.stairAngle * (Math.PI / 180));
        const idealRiser: number = norms.treadDepthIdeal * tanA;
        const clampedRiser: number = Math.max(norms.riserHeightMin, Math.min(norms.riserHeightMax, idealRiser));
        const stepCount: number = Math.max(3, Math.round(totalRise / clampedRiser));
        const riserHeight: number = totalRise / stepCount;
        const treadDepth: number = config.treadDepth ?? riserHeight / tanA;
        return { stepCount, riserHeight, treadDepth };
    }
    const baseRiser: number = config.riserHeight ?? norms.riserHeightIdeal;
    const stepCount: number = Math.max(3, Math.round(totalRise / baseRiser));
    const riserHeight: number = totalRise / stepCount;
    const rawTreadDepth: number = config.treadDepth ?? norms.blondelIdeal - 2 * riserHeight;
    const treadDepth: number = Math.max(norms.treadDepthMin, Math.min(norms.treadDepthMax, rawTreadDepth));
    return { stepCount, riserHeight, treadDepth };
}

export function calculateLayout(config: IStairConfig, norms: IStairNorms): IUShapeLayout {
    const totalRise: number = config.floorHeight + config.floorThickness;

    let { stepCount, riserHeight, treadDepth } = resolveStepDimensions(config, norms, totalRise);

    const turnStepCount: number = config.turnType === 'landing' ? 1 : U_WINDER_COUNT;
    let remainingTreads: number = stepCount - 1 - turnStepCount;

    if (remainingTreads < 2) {
        remainingTreads = 2;
        stepCount = remainingTreads + 1 + turnStepCount;
        riserHeight = totalRise / stepCount;
    }

    // Flight 2 runs along -Z, constrained by opening length. The turn zone
    // sits between flight 2's top and the opening's far edge, so we subtract
    // its z-extent — sw for a landing, sw + flightGap/2 for winders (the
    // outer-radius bulge). Without this, winder geometry pushes flight 2's
    // bottom past the opening's near edge, leaving the last tread under the
    // solid slab.
    const turnZoneRunZ: number = config.turnType === 'winder' ? config.stairWidth + config.flightGap / 2 : config.stairWidth;
    const spaceByOpening: number = config.openingLength - turnZoneRunZ;
    const maxFlight2Run: number = Math.max(0, spaceByOpening);

    // STRATEGY: flight 2's tread is force-fit to the opening, so its count and the total
    // step count decide riser, tread, Blondel and angle together — `resolveTurnSplit` picks
    // them jointly to land every check in norm (a pinned turnAtStep fixes flight 1).
    const split: ITurnStepSplit = resolveTurnSplit(config, norms, { totalRise, maxFlight2Run, turnStepCount, stepCount });
    const { flight1Count, flight2Count } = split;
    if (split.stepCount !== stepCount) {
        stepCount = split.stepCount;
        riserHeight = totalRise / stepCount;
    }

    // FORCE-FIT: flight 2 attaches to opening edge exactly
    if (maxFlight2Run > 0 && flight2Count > 0) {
        treadDepth = maxFlight2Run / flight2Count;
    }

    return { stepCount, riserHeight, treadDepth, flight1Count, flight2Count, turnStepCount };
}
